import { Component } from '@theme/component';
import { getBundleStore } from '@theme/pdp-bundle-store';

/**
 * @typedef {object} SubscriptionPlan
 * @property {number} id - Withings+ VARIANT id (the line we add — no selling plan)
 * @property {string} name - Variant title (e.g. "Annuel")
 * @property {string} term - Subscription term for the backend ("yearly" | "monthly")
 * @property {string} trialPeriod - Term duration in seconds (variant `trial_period` metafield) or empty
 * @property {string} sellingPlanId - Selling plan id tied to the variant (Withings drives the contract via API) or empty
 * @property {string} label - Pre-formatted label (e.g. "Annuel : 99,95 € / an")
 * @property {number} priceCents - Variant price, in minor units. Informational only: the
 *   order summary always contributes 0 for this line (see `getDisplay`).
 * @property {string} priceHtml - Pre-formatted money HTML (empty when the variant is priced
 *   at 0). Informational only, for the same reason.
 * @property {string} discountText - Pre-rendered discount label (e.g. "-17%") or empty
 *
 * @typedef {object} BlockRefs
 * @property {HTMLElement} [subscribeRow] - Wrapper of the subscribe option (for `is-selected` toggle)
 * @property {HTMLElement} [noSubscribeRow] - Wrapper of the no-subscribe option
 * @property {HTMLInputElement} [subscribeRadio] - "Subscription" radio
 * @property {HTMLInputElement} [noSubscribeRadio] - "No subscription" radio
 * @property {HTMLDetailsElement} [planSelect] - Custom dropdown wrapping the plan list
 * @property {HTMLElement} [planLabel] - Trigger label that shows the current plan
 */

/**
 * PDP block that owns the Withings+ subscription selection.
 *
 * The subscription is a SEPARATE product (resolved from the eligible product's
 * `subscription_sku` metafield) added as a NESTED cart line under the device
 * line. The monthly/annual choice is a VARIANT of that product (no Shopify
 * selling plan). This contributor:
 *  - `getItems()` adds the chosen Withings+ variant line + the
 *    `_subscription_term` / `_trial_period` properties; Withings handles
 *    renewals post-order.
 *  - `transformPayload()` stamps a shared `_sales_line_item_group_id` (= the
 *    bundle key) on BOTH the main device line and the subscription line, and
 *    sets `parent_id` on the subscription line so Shopify nests it under the
 *    device (native removal cascade + grouped checkout display).
 *
 * One subscription per DEVICE LINE — each add creates its own device + nested
 * subscription pair, so no cart-level guard is needed.
 *
 * @extends Component<BlockRefs>
 */
class SubscriptionBlock extends Component {
  /** @type {SubscriptionPlan | null} - Currently committed plan (null when "No subscription"). */
  #plan = null;

  /** @type {string} - Default variant id (pre-selected yearly). */
  #defaultPlanId = '';

  /** @type {string} - Withings+ product title (order summary row label). */
  #subTitle = '';

  /** @type {(() => void) | null} */
  #unregisterContributor = null;

  connectedCallback() {
    super.connectedCallback();
    this.#defaultPlanId = this.dataset.defaultPlan ?? '';
    this.#subTitle = this.dataset.subTitle ?? '';

    this.#hydrateDefaultPlan();
    this.#syncDiscountBadge();
    this.#registerWithStore();
    document.addEventListener('model:update', this.#onModelUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#unregisterContributor?.();
    this.#unregisterContributor = null;
    document.removeEventListener('model:update', this.#onModelUpdate);
  }

  #onModelUpdate = () => {
    // Refresh main context (product/section may have changed) and re-hydrate
    // the default plan from the freshly-morphed DOM. `this.refs` is unsafe
    // until the MutationObserver re-pairs nodes — query the DOM directly here.
    // See .claude/rules/pdp-morph.md.
    const store = getBundleStore();
    store.setContext({
      productId: this.dataset.productId ?? '',
      sectionId: this.dataset.sectionId ?? '',
    });
    this.#defaultPlanId = this.dataset.defaultPlan ?? '';
    this.#subTitle = this.dataset.subTitle ?? '';
    this.#plan = null;

    this.#hydrateDefaultPlan();
    store.publish('subscription');
    this.#syncOptionStyles();
    this.#syncDiscountBadge();
  };

  #registerWithStore() {
    const store = getBundleStore();
    store.setContext({
      productId: this.dataset.productId ?? '',
      sectionId: this.dataset.sectionId ?? '',
    });

    this.#unregisterContributor = store.register({
      name: 'subscription',
      getSectionId: () => this.dataset.sectionId ?? '',
      // Positive order — the subscription is now an ADDITION (its own line),
      // applied before band / cross-sell. See docs/pdp-bundle-store.md.
      order: 5,
      getItems: () => {
        if (!this.#plan) return [];
        // One item = the chosen Withings+ variant + its selling plan (creates
        // the SubscriptionContract Withings then drives via API). The term +
        // trial period (seconds) go to the backend as properties.
        /** @type {Record<string, string>} */
        const properties = { _bundle_role: 'subscription', _subscription_term: this.#plan.term };
        if (this.#plan.trialPeriod) properties._trial_period = this.#plan.trialPeriod;
        /** @type {{ id: number, quantity: number, properties: Record<string, string>, selling_plan?: number }} */
        const item = { id: this.#plan.id, quantity: 1, properties };
        if (this.#plan.sellingPlanId) item.selling_plan = Number(this.#plan.sellingPlanId);
        return [item];
      },
      getDisplay: () => {
        if (!this.#plan) return [];
        return [
          {
            role: 'subscription',
            title: this.#subTitle || this.#plan.name,
            subtitle: this.#plan.label,
            // Withings+ is never charged at checkout: the selling plan's first
            // cycle is free (trial), so the line contributes 0 to the bundle
            // total and shows no price — only `subtitle` carries the plan price.
            // Hardcoded rather than read from the plan: the variant is priced at
            // 0 TODAY, but the day Withings puts a real per-market price on it
            // (so the renewal amount stops being a converted USD figure), this
            // line must keep totalling 0. The real price then only feeds the
            // plan label and the annual discount calc, both computed in Liquid.
            priceCents: 0,
            priceHtml: '',
            variantId: String(this.#plan.id),
          },
        ];
      },
      transformPayload: (items, ctx) => {
        if (!this.#plan) return items;
        // Shared linking key on BOTH the device (main) line and the subscription
        // line. Reuse the bundle key — device + subscription are added in the
        // same batch, so they already share one UUID. `parent_id` nests the
        // subscription under the device line (native removal cascade).
        const groupId = ctx.bundleKey;
        const mainVariantId = Number(ctx.mainVariantId);
        for (const it of items) {
          const role = it.properties?._bundle_role;
          if (role === 'main' || role === 'subscription') {
            it.properties = { ...(it.properties ?? {}), _sales_line_item_group_id: groupId };
          }
          if (role === 'subscription' && mainVariantId) {
            it.parent_id = mainVariantId;
          }
        }
        return items;
      },
      // No onModelUpdate hook: the component's own `model:update` listener
      // (#onModelUpdate) resets the plan AND re-reads the fresh dataset first.
      onCommit: () => {
        // After a successful add-to-cart, restore the initial selection so the
        // next add picks up where the customer left off (yearly + subscribe).
        this.#plan = null;
        this.#hydrateDefaultPlan();
        this.#syncRadiosToSelection();
        this.#syncOptionStyles();
        this.#syncDiscountBadge();
        getBundleStore().publish('subscription');
      },
    });
  }

  /**
   * Read the default plan from the freshly-rendered DOM and set `#plan` + the
   * dropdown trigger/aria in sync. Falls back to the first plan option.
   */
  #hydrateDefaultPlan() {
    if (!this.#defaultPlanId) return;

    const matched = this.querySelector(
      `.subscription-block__plan-option[data-plan-id="${this.#defaultPlanId}"]`
    );
    /** @type {HTMLElement | null} */
    let button = matched instanceof HTMLElement ? matched : null;
    if (!button) {
      const first = this.querySelector('.subscription-block__plan-option');
      button = first instanceof HTMLElement ? first : null;
    }
    if (!button) return;

    this.#plan = this.#planFromButton(button);
    this.#syncTriggerLabel();
    this.#syncOptionAriaSelected(button);
  }

  /**
   * Update the floating discount badge based on the dropdown's currently
   * `aria-selected` plan option — independent from `this.#plan`. Idempotent.
   */
  #syncDiscountBadge() {
    const badge = this.querySelector('[ref="discountBadge"]');
    if (!(badge instanceof HTMLElement)) return;

    const activeOption = this.querySelector(
      '.subscription-block__plan-option[aria-selected="true"]'
    );
    const text =
      activeOption instanceof HTMLElement ? (activeOption.dataset.planDiscountText ?? '') : '';

    if (!text) {
      badge.hidden = true;
      badge.textContent = '';
      return;
    }
    badge.hidden = false;
    badge.textContent = text;
  }

  /** Mirror the active plan's label into the dropdown trigger. */
  #syncTriggerLabel() {
    const label = this.querySelector('[ref="planLabel"]');
    if (!(label instanceof HTMLElement)) return;
    label.textContent = this.#plan?.label ?? '';
  }

  /**
   * Flip `aria-selected` on every plan option button so AT clients see the
   * single active option.
   * @param {HTMLElement | null} activeButton
   */
  #syncOptionAriaSelected(activeButton) {
    const buttons = this.querySelectorAll('.subscription-block__plan-option');
    for (const opt of buttons) {
      if (!(opt instanceof HTMLElement)) continue;
      opt.setAttribute('aria-selected', opt === activeButton ? 'true' : 'false');
    }
  }

  /**
   * @param {HTMLElement} button - Plan option button rendered by the snippet.
   * @returns {SubscriptionPlan | null}
   */
  #planFromButton(button) {
    const id = Number(button.dataset.planId);
    if (!id) return null;
    return {
      id,
      name: button.dataset.planName ?? '',
      term: button.dataset.planTerm ?? '',
      trialPeriod: button.dataset.planTrialPeriod ?? '',
      sellingPlanId: button.dataset.planSellingPlanId ?? '',
      label: button.dataset.planLabel ?? '',
      priceCents: Number(button.dataset.planPriceCents) || 0,
      priceHtml: button.dataset.planPriceHtml ?? '',
      discountText: button.dataset.planDiscountText ?? '',
    };
  }

  /**
   * Public event handler — toggles between Subscribe and No subscription.
   * @param {Event} event
   */
  handleSubscribeChange(event) {
    const target = event.target;
    if (!(target instanceof HTMLInputElement)) return;

    const subscribe = this.querySelector('[ref="subscribeRadio"]');
    const noSubscribe = this.querySelector('[ref="noSubscribeRadio"]');

    if (target.value === 'subscribe' && target.checked) {
      if (noSubscribe instanceof HTMLInputElement) noSubscribe.checked = false;
      const active = this.querySelector('.subscription-block__plan-option[aria-selected="true"]');
      if (active instanceof HTMLElement) {
        this.#plan = this.#planFromButton(active);
      } else {
        this.#hydrateDefaultPlan();
      }
    } else if (target.value === 'none' && target.checked) {
      if (subscribe instanceof HTMLInputElement) subscribe.checked = false;
      this.#plan = null;
    }

    this.#syncOptionStyles();
    this.#syncDiscountBadge();
    getBundleStore().publish('subscription');
  }

  /**
   * Public event handler — Enter on a focused radio checks it (native radios
   * only check on Space). Space keeps its native behavior.
   * @param {KeyboardEvent} event
   */
  handleRadioKeydown(event) {
    if (event.key !== 'Enter') return;
    const target = event.target;
    if (!(target instanceof HTMLInputElement)) return;

    event.preventDefault();
    if (target.checked) return;

    target.checked = true;
    target.dispatchEvent(new Event('change', { bubbles: true }));
  }

  /**
   * Public event handler — fires when the user picks a plan from the dropdown.
   * Wired via `on:click="/handlePlanSelect/{plan.id}"`.
   * @param {string} planId - Subscription variant id from the option button URL (no selling plan).
   */
  handlePlanSelect(planId) {
    const button = this.querySelector(
      `.subscription-block__plan-option[data-plan-id="${planId}"]`
    );
    if (!(button instanceof HTMLElement)) return;

    const next = this.#planFromButton(button);
    if (!next) return;
    this.#plan = next;

    const dropdown = this.querySelector('[ref="planSelect"]');
    if (dropdown instanceof HTMLDetailsElement) dropdown.removeAttribute('open');

    this.#syncTriggerLabel();
    this.#syncOptionAriaSelected(button);

    // Picking a plan implicitly means "I want to subscribe".
    const subscribeRadio = this.querySelector('[ref="subscribeRadio"]');
    if (subscribeRadio instanceof HTMLInputElement && !subscribeRadio.checked) {
      subscribeRadio.checked = true;
    }
    const noSubscribeRadio = this.querySelector('[ref="noSubscribeRadio"]');
    if (noSubscribeRadio instanceof HTMLInputElement) noSubscribeRadio.checked = false;

    this.#syncOptionStyles();
    this.#syncDiscountBadge();
    getBundleStore().publish('subscription');
  }

  /**
   * Re-check the right radio when state was reset programmatically. Idempotent.
   */
  #syncRadiosToSelection() {
    const subscribe = this.querySelector('[ref="subscribeRadio"]');
    const noSubscribe = this.querySelector('[ref="noSubscribeRadio"]');
    const wantsSubscribe = this.#plan !== null;

    if (subscribe instanceof HTMLInputElement) subscribe.checked = wantsSubscribe;
    if (noSubscribe instanceof HTMLInputElement) noSubscribe.checked = !wantsSubscribe;
  }

  /**
   * Toggle the `is-selected` class on each option wrapper to drive the purple
   * border styling. Querying the DOM (not refs) so it works pre/post-morph.
   */
  #syncOptionStyles() {
    const subscribeRow = this.querySelector('.subscription-block__option--subscribe');
    const noSubscribeRow = this.querySelector('.subscription-block__option--no-subscribe');
    const wantsSubscribe = this.#plan !== null;

    if (subscribeRow instanceof HTMLElement) {
      subscribeRow.classList.toggle('is-selected', wantsSubscribe);
    }
    if (noSubscribeRow instanceof HTMLElement) {
      noSubscribeRow.classList.toggle('is-selected', !wantsSubscribe);
    }
  }
}

if (!customElements.get('subscription-block')) {
  customElements.define('subscription-block', SubscriptionBlock);
}
